import { propState, type Command, type EntityState, type LayerTerrain, type PropState } from "../document/commands.ts";
import { sameJson } from "../document/same-json.ts";
import type { EntityInstance, JsonValue, PropBehaviour, PropInstance, TileRef, WorldDocument } from "../document/types.ts";
import type { IntentOf, KeyChanges } from "./op-types.ts";
import { LAYER_ITEMS_MAX, TERRAIN_NAMES_MAX, layerOf, mapOf, passesFieldLimits, type Resolution } from "./resolve-types.ts";

/**
 * Resolving the intents that write one field or one item (ADR-086 decision 19): a name, a layer's group or
 * terrain, a terrain's name, a map's properties per key, a prop's fields, an entity's position and each key of
 * its fields, and props and entities added by id and neighbour or removed by id. Each reads what it replaces
 * from the document, so the command it gives always applies, and the later of two writes of one key stands.
 */

/**
 * A record with keys set and removed, its keys in order so equal records are equal text.
 *
 * @param before The record as held.
 * @param changes The keys to set and to remove.
 * @returns The new record.
 */
function withKeys(before: { readonly [key: string]: JsonValue }, changes: KeyChanges): { readonly [key: string]: JsonValue } {
  const next = new Map<string, JsonValue>(Object.entries(before));
  for (const [key, value] of Object.entries(changes.set ?? {})) {
    next.set(key, value);
  }
  for (const key of changes.remove ?? []) {
    next.delete(key);
  }
  return Object.fromEntries([...next].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)));
}

/**
 * Whether every frame a prop draws is on a tileset the world lists: its own, each state's and each animation's.
 *
 * @param world The document.
 * @param frame The prop's frame.
 * @param behaviour Its behaviour, if any.
 * @returns True when all are listed.
 */
function framesListed(world: WorldDocument, frame: TileRef, behaviour: PropBehaviour | undefined): boolean {
  const frames = [frame, ...Object.values(behaviour?.frames ?? {}), ...Object.values(behaviour?.animations ?? {})];
  return frames.every((tile) => world.tilesets.some((tileset) => tileset.id === tile.tileset));
}

/**
 * Whether a position is on the tile's pixel lattice, as a saved prop's must be (ADR-027).
 *
 * @param world The document, for its tile size.
 * @param x The position across, in tiles.
 * @param y The position down, in tiles.
 * @returns True when both are multiples of one pixel.
 */
function onLattice(world: WorldDocument, x: number, y: number): boolean {
  return Number.isInteger(x * world.tileSize) && Number.isInteger(y * world.tileSize);
}

/**
 * Where an item goes in its list: first with no neighbour, right after its neighbour, last when the neighbour
 * is gone.
 *
 * @param items The list.
 * @param after The neighbour's id, or null.
 * @returns The index.
 */
function placeAfter(items: readonly { readonly id: string }[], after: string | null): number {
  if (after === null) {
    return 0;
  }
  const at = items.findIndex((item) => item.id === after);
  return at < 0 ? items.length : at + 1;
}

/**
 * Resolves a map's rename.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command, nothing when the map holds the name, or `gone`.
 */
export function resolveRenameMap(world: WorldDocument, intent: IntentOf<"renameMap">): Resolution {
  const map = mapOf(world, intent.map);
  if (map === undefined) {
    return "gone";
  }
  return map.name === intent.name ? [] : [{ kind: "renameMap", mapId: map.id, before: map.name, after: intent.name }];
}

/**
 * Resolves a layer's rename, with its `autoName` flag when the intent carries one and the layer holds another.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command, nothing when the layer holds the name and the flag, `gone`, or `kind` for a flag on a
 *   layer that is not terrain.
 */
export function resolveRenameLayer(world: WorldDocument, intent: IntentOf<"renameLayer">): Resolution {
  const layer = layerOf(world, intent.map, intent.layer);
  if (layer === undefined) {
    return "gone";
  }
  const base = { kind: "renameLayer" as const, mapId: intent.map, layerId: layer.id, before: layer.name, after: intent.name };
  if (intent.autoName === undefined) {
    return layer.name === intent.name ? [] : [base];
  }
  if (layer.kind !== "terrain") {
    return "kind";
  }
  const flag = intent.autoName === null ? undefined : intent.autoName;
  if (layer.autoName === flag) {
    return layer.name === intent.name ? [] : [base];
  }
  return [{ ...base, autoName: { ...(layer.autoName === undefined ? {} : { before: layer.autoName }), ...(flag === undefined ? {} : { after: flag }) } }];
}

/**
 * Resolves a group's rename.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command, nothing when the group holds the name, or `gone`.
 */
export function resolveRenameGroup(world: WorldDocument, intent: IntentOf<"renameGroup">): Resolution {
  const group = (mapOf(world, intent.map)?.groups ?? []).find((candidate) => candidate.id === intent.group);
  if (group === undefined) {
    return "gone";
  }
  return group.name === intent.name ? [] : [{ kind: "renameGroup", mapId: intent.map, groupId: group.id, before: group.name, after: intent.name }];
}

/**
 * Resolves a layer's move into a group or out of every group.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command, nothing when the layer is already there, or `gone` when the layer or the named group
 *   is.
 */
export function resolveSetLayerGroup(world: WorldDocument, intent: IntentOf<"setLayerGroup">): Resolution {
  const map = mapOf(world, intent.map);
  const layer = layerOf(world, intent.map, intent.layer);
  if (map === undefined || layer === undefined || (intent.group !== null && !(map.groups ?? []).some((group) => group.id === intent.group))) {
    return "gone";
  }
  const after = intent.group ?? undefined;
  return layer.group === after ? [] : [{ kind: "setLayerGroup", mapId: map.id, layerId: layer.id, before: layer.group, after }];
}

/**
 * Resolves a terrain layer's change of terrain: terrain, variant, name and face height as one value.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command, nothing when the layer holds all of it, `gone`, or `kind` for a layer that is not
 *   terrain.
 */
export function resolveSetLayerTerrain(world: WorldDocument, intent: IntentOf<"setLayerTerrain">): Resolution {
  const layer = layerOf(world, intent.map, intent.layer);
  if (layer === undefined) {
    return "gone";
  }
  if (layer.kind !== "terrain") {
    return "kind";
  }
  if (layer.terrain === intent.terrain && layer.variant === intent.variant && (intent.name === undefined || layer.name === intent.name) && layer.faceHeight === intent.faceHeight) {
    return [];
  }
  const before: LayerTerrain = { terrain: layer.terrain, variant: layer.variant, ...(intent.name === undefined ? {} : { name: layer.name }), ...(layer.faceHeight === undefined ? {} : { faceHeight: layer.faceHeight }) };
  const after: LayerTerrain = { terrain: intent.terrain, variant: intent.variant, ...(intent.name === undefined ? {} : { name: intent.name }), ...(intent.faceHeight === undefined ? {} : { faceHeight: intent.faceHeight }) };
  return [{ kind: "setLayerTerrain", mapId: intent.map, layerId: layer.id, before, after }];
}

/**
 * Resolves a terrain layer's face height.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command, nothing when the layer holds the height, `gone`, or `kind` for a layer that is not
 *   terrain.
 */
export function resolveSetLayerFaceHeight(world: WorldDocument, intent: IntentOf<"setLayerFaceHeight">): Resolution {
  const layer = layerOf(world, intent.map, intent.layer);
  if (layer === undefined) {
    return "gone";
  }
  if (layer.kind !== "terrain") {
    return "kind";
  }
  const after = intent.height ?? undefined;
  if (layer.faceHeight === after) {
    return [];
  }
  return [{ kind: "setLayerFaceHeight", mapId: intent.map, layerId: layer.id, ...(layer.faceHeight === undefined ? {} : { before: layer.faceHeight }), ...(after === undefined ? {} : { after }) }];
}

/**
 * Resolves the author's name for a terrain entry.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command, nothing when the entry holds the name or is already unnamed, `limit` for a new entry
 *   past `TERRAIN_NAMES_MAX`, or `invalid` for an entry whose key reads an inherited name, which the document
 *   cannot hold.
 */
export function resolveSetTerrainName(world: WorldDocument, intent: IntentOf<"setTerrainName">): Resolution {
  const names = world.terrainNames ?? {};
  const held = Object.hasOwn(names, intent.entry);
  if (!held && (names as Record<string, unknown>)[intent.entry] !== undefined) {
    return "invalid";
  }
  const before = held ? names[intent.entry] : undefined;
  const after = intent.name ?? undefined;
  if (before === after) {
    return [];
  }
  if (!held && Object.keys(names).length >= TERRAIN_NAMES_MAX) {
    return "limit";
  }
  return [{ kind: "setTerrainName", entry: intent.entry, ...(before === undefined ? {} : { before }), ...(after === undefined ? {} : { after }) }];
}

/**
 * Resolves a change of a map's properties, key by key.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command carrying the whole record before and after, nothing when every key already holds its
 *   value, `gone`, or `limit`.
 */
export function resolveSetMapProperties(world: WorldDocument, intent: IntentOf<"setMapProperties">): Resolution {
  const map = mapOf(world, intent.map);
  if (map === undefined) {
    return "gone";
  }
  const after = withKeys(map.properties, intent);
  if (sameJson(map.properties, after)) {
    return [];
  }
  return passesFieldLimits(map.properties, after) ? "limit" : [{ kind: "setMapProperties", mapId: map.id, before: map.properties, after }];
}

/**
 * Resolves a prop's update: the fields the intent names over the state the document holds.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command, nothing when the prop holds every named field, `gone`, `kind` for a layer that holds
 *   no props, `invalid` for a position off the pixel lattice, `tileset` for a frame the world does not list,
 *   or `limit` for a behaviour whose fields would pass theirs.
 */
export function resolveUpdateProp(world: WorldDocument, intent: IntentOf<"updateProp">): Resolution {
  const layer = layerOf(world, intent.map, intent.layer);
  if (layer === undefined) {
    return "gone";
  }
  if (layer.kind !== "prop") {
    return "kind";
  }
  const prop = layer.props.find((candidate) => candidate.id === intent.prop);
  if (prop === undefined) {
    return "gone";
  }
  const before = propState(prop);
  const behaviour = intent.behaviour === undefined ? before.behaviour : (intent.behaviour ?? undefined);
  const reframed = intent.frame !== undefined && !sameJson(intent.frame, before.frame);
  const footprint = intent.footprint === undefined ? (reframed || behaviour !== undefined ? undefined : before.footprint) : (intent.footprint ?? undefined);
  if (footprint !== undefined && (behaviour !== undefined || (reframed && sameJson(footprint, before.footprint)))) {
    return "invalid";
  }
  const keepPlacement = intent.keepPlacement ?? before.keepPlacement === true;
  const after: PropState = {
    frame: intent.frame ?? before.frame,
    x: intent.at === undefined ? before.x : intent.at[0],
    y: intent.at === undefined ? before.y : intent.at[1],
    flipX: intent.flipX ?? before.flipX,
    flipY: intent.flipY ?? before.flipY,
    sortPixels: intent.sortPixels ?? before.sortPixels,
    ...(behaviour === undefined ? {} : { behaviour }),
    ...(keepPlacement ? { keepPlacement: true as const } : {}),
    ...(footprint === undefined ? {} : { footprint }),
  };
  if (sameJson(before, after)) {
    return [];
  }
  if (intent.at !== undefined && !onLattice(world, after.x, after.y)) {
    return "invalid";
  }
  if (!framesListed(world, after.frame, after.behaviour)) {
    return "tileset";
  }
  if (intent.behaviour !== undefined && after.behaviour !== undefined && passesFieldLimits(before.behaviour?.fields, after.behaviour.fields)) {
    return "limit";
  }
  return [{ kind: "updateProp", mapId: intent.map, layerId: layer.id, propId: prop.id, before, after }];
}

/**
 * Resolves an entity's update: its position and each named key of its fields.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command, nothing when the entity holds all of it, `gone`, `kind` for a layer that holds no
 *   entities, or `limit`.
 */
export function resolveUpdateEntity(world: WorldDocument, intent: IntentOf<"updateEntity">): Resolution {
  const layer = layerOf(world, intent.map, intent.layer);
  if (layer === undefined) {
    return "gone";
  }
  if (layer.kind !== "entity") {
    return "kind";
  }
  const entity = layer.entities.find((candidate) => candidate.id === intent.entity);
  if (entity === undefined) {
    return "gone";
  }
  const before: EntityState = { x: entity.x, y: entity.y, fields: entity.fields };
  const fields = intent.set === undefined && intent.remove === undefined ? entity.fields : withKeys(entity.fields, intent);
  const after: EntityState = { x: intent.at === undefined ? entity.x : intent.at[0], y: intent.at === undefined ? entity.y : intent.at[1], fields };
  if (sameJson(before, after)) {
    return [];
  }
  return passesFieldLimits(entity.fields, fields) ? "limit" : [{ kind: "updateEntity", mapId: intent.map, layerId: layer.id, entityId: entity.id, before, after }];
}

/**
 * What an add resolves to before its own checks: nothing when the item is already there as carried, `taken`
 * when another holds its id, `limit` when the layer is full.
 *
 * @param items The layer's items.
 * @param item The item to add.
 * @returns The resolution, or undefined when the add may go on.
 */
function addBlocked(items: readonly (PropInstance | EntityInstance)[], item: PropInstance | EntityInstance): Resolution | undefined {
  const existing = items.find((candidate) => candidate.id === item.id);
  if (existing !== undefined) {
    return sameJson(existing, item) ? [] : "taken";
  }
  return items.length >= LAYER_ITEMS_MAX ? "limit" : undefined;
}

/**
 * Resolves a prop's add by id and neighbour.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command with the prop's index, nothing when the prop is already there as carried, or the
 *   reason: `gone`, `kind`, `taken`, `limit` for a full layer or a behaviour's fields past theirs, `invalid`
 *   for a position off the pixel lattice, `tileset`.
 */
export function resolveAddProp(world: WorldDocument, intent: IntentOf<"addProp">): Resolution {
  const layer = layerOf(world, intent.map, intent.layer);
  if (layer === undefined) {
    return "gone";
  }
  if (layer.kind !== "prop") {
    return "kind";
  }
  const blocked = addBlocked(layer.props, intent.prop);
  if (blocked !== undefined) {
    return blocked;
  }
  if (!onLattice(world, intent.prop.x, intent.prop.y)) {
    return "invalid";
  }
  if (!framesListed(world, intent.prop.frame, intent.prop.behaviour)) {
    return "tileset";
  }
  if (intent.prop.behaviour !== undefined && passesFieldLimits(undefined, intent.prop.behaviour.fields)) {
    return "limit";
  }
  return [{ kind: "addProp", mapId: intent.map, layerId: layer.id, index: placeAfter(layer.props, intent.after), prop: intent.prop }];
}

/**
 * Resolves an entity's add by id and neighbour.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command with the entity's index, nothing when the entity is already there as carried, or the
 *   reason: `gone`, `kind`, `taken`, `limit` for a full layer or fields past a limit.
 */
export function resolveAddEntity(world: WorldDocument, intent: IntentOf<"addEntity">): Resolution {
  const layer = layerOf(world, intent.map, intent.layer);
  if (layer === undefined) {
    return "gone";
  }
  if (layer.kind !== "entity") {
    return "kind";
  }
  const blocked = addBlocked(layer.entities, intent.entity) ?? (passesFieldLimits(undefined, intent.entity.fields) ? "limit" : undefined);
  if (blocked !== undefined) {
    return blocked;
  }
  return [{ kind: "addEntity", mapId: intent.map, layerId: layer.id, index: placeAfter(layer.entities, intent.after), entity: intent.entity }];
}

/**
 * Resolves a prop's or an entity's removal by id. An entity's removal that names a type takes the entity
 * only while it is of that type: a removal is of the entity its maker saw, never of another that has since
 * been put in its place under the same id.
 *
 * @param world The document.
 * @param intent The intent.
 * @returns The command carrying the item as held; nothing when it is already gone, with its layer or
 *   without; or `taken` when the id holds an entity of another type than the one named.
 */
export function resolveRemoveItem(world: WorldDocument, intent: IntentOf<"removeProp"> | IntentOf<"removeEntity">): Resolution {
  const layer = layerOf(world, intent.map, intent.layer);
  const at = { mapId: intent.map, layerId: intent.layer };
  if (intent.k === "removeProp" && layer?.kind === "prop") {
    const index = layer.props.findIndex((candidate) => candidate.id === intent.prop);
    return index < 0 ? [] : [{ kind: "removeProp", ...at, index, prop: layer.props[index] as PropInstance } satisfies Command];
  }
  if (intent.k === "removeEntity" && layer?.kind === "entity") {
    const index = layer.entities.findIndex((candidate) => candidate.id === intent.entity);
    const held = layer.entities[index];
    if (held === undefined) {
      return [];
    }
    return intent.type !== undefined && held.type !== intent.type ? "taken" : [{ kind: "removeEntity", ...at, index, entity: held } satisfies Command];
  }
  return [];
}
